/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { keyframes } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import type MediaReducedMotion from '@atlaskit/css/at-rules/media-reduced-motion';
import Image from '@atlaskit/image/image';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import { MorphingShapeLoader } from '@atlassian/rovo-platform-ui-components/primitives/MorphingShapeLoader';

import ReviewCheck from './assets/ReviewCheck.svg';
import ReviewCheckSparkle from './assets/ReviewCheckSparkle.svg';

// Animate only the indicator. The agent keeps the same unanimated slot in both states.
const checkExit = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});
const sparkleToLoading = keyframes({
	from: { opacity: 1, transform: 'translate(0, 0) rotate(0deg) scale(1)' },
	// Only opacity has a midpoint: keep transform on one uninterrupted easing curve.
	'50%': { opacity: 1 },
	to: { opacity: 0, transform: 'translate(-24px, -12px) rotate(180deg) scale(1.6)' },
});
const loadingEnter = keyframes({
	from: { opacity: 0, transform: 'rotate(-90deg) scale(0.4)' },
	'35%': { opacity: 0 },
	to: { opacity: 1, transform: 'rotate(0deg) scale(1)' },
});

const styles = cssMap({
	container: {
		position: 'relative',
		width: '96px',
		height: '56px',
		flexShrink: 0,
	},
	agent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '56px',
		height: '56px',
	},
	avatar: {
		display: 'flex',
		// Render at 56px; AgentAvatar's adjacent sizes are 40px and 96px.
		transform: 'rotate(-15deg) scale(1.4)',
	},
	indicator: {
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineEnd: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '48px',
		height: '100%',
	},
	loadingOverlap: {
		insetInlineEnd: token('space.150'),
	},
	sparkle: {
		position: 'absolute',
		insetInlineEnd: 0,
		insetBlockStart: token('space.400'),
		display: 'flex',
		width: '20px',
		height: '20px',
		pointerEvents: 'none',
	},
	sparkleTransition: {
		animationName: sparkleToLoading,
		animationDuration: token('motion.duration.xxlong'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationFillMode: 'both',
		['@media (prefers-reduced-motion: reduce)' satisfies MediaReducedMotion]: {
			display: 'none',
		},
	},
	exit: {
		animationName: checkExit,
		animationDuration: token('motion.duration.xlong'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationFillMode: 'both',
		['@media (prefers-reduced-motion: reduce)' satisfies MediaReducedMotion]: {
			display: 'none',
		},
	},
	enter: {
		animationName: loadingEnter,
		animationDuration: token('motion.duration.xxlong'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationFillMode: 'both',
		['@media (prefers-reduced-motion: reduce)' satisfies MediaReducedMotion]: {
			animationName: 'none',
		},
	},
});

export const SuggestionsReviewIllustration = ({
	hasShownInitialState = false,
	isLoading = false,
}: {
	hasShownInitialState?: boolean;
	isLoading?: boolean;
}): React.JSX.Element => {
	return (
		<div css={styles.container}>
			<div css={styles.agent}>
				<span css={styles.avatar} aria-hidden>
					<AgentAvatar agentNamedId="content_reviewer_agent" size="large" showBorder={false} />
				</span>
			</div>
			<div aria-hidden>
				{(!isLoading || hasShownInitialState) && (
					<React.Fragment>
						<div css={[styles.indicator, isLoading && styles.exit]}>
							<Image src={ReviewCheck} width={48} height={48} alt="" />
						</div>
						<span css={[styles.sparkle, isLoading && styles.sparkleTransition]}>
							<Image src={ReviewCheckSparkle} width={20} height={20} alt="" />
						</span>
					</React.Fragment>
				)}
				{isLoading && (
					<div
						css={[styles.indicator, styles.loadingOverlap, hasShownInitialState && styles.enter]}
					>
						<MorphingShapeLoader testId="suggestions-panel-thinking-indicator" size="large" />
					</div>
				)}
			</div>
		</div>
	);
};
